import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal, Input, Textarea, Button, Alert } from '@components/common'
import { PermissionEditor } from '@components/features/permissions'
import { useRoleStore } from '@store'
import './Roles.css'

/**
 * Role Form Validation Schema
 */
const roleSchema = z.object({
	name: z.string().min(3, 'Name must be at least 3 characters').max(100, 'Name too long'),
	slug: z
		.string()
		.min(3, 'Slug must be at least 3 characters')
		.max(100, 'Slug too long')
		.regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
	description: z.string().min(10, 'Description must be at least 10 characters').optional().or(z.literal('')),
})

/**
 * RoleForm Component
 *
 * Modal form for creating and editing roles with permission management
 *
 * @param {Object} props
 * @param {string} props.mode - Form mode ('create' or 'edit')
 * @param {Function} props.onClose - Close handler
 */
const RoleForm = ({ mode = 'create', onClose }) => {
	const { selectedRole, createRole, updateRole, loading } = useRoleStore()
	const isEditMode = mode === 'edit'
	const isSystemRole = selectedRole?.is_system_default === true || selectedRole?.is_system_default === 1 || selectedRole?.is_system_default === '1'

	// Permissions state (separate from react-hook-form due to complex structure)
	const [permissions, setPermissions] = useState({})
	const [activeTab, setActiveTab] = useState('details')

	// React Hook Form setup
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
	} = useForm({
		resolver: zodResolver(roleSchema),
		defaultValues: {
			name: '',
			slug: '',
			description: '',
		},
	})

	// Populate form in edit mode
	useEffect(() => {
		if (isEditMode && selectedRole) {
			setValue('name', selectedRole.name || '')
			setValue('slug', selectedRole.slug || '')
			setValue('description', selectedRole.description || '')
			// Parse permissions - handle both string and object
			const rolePermissions = typeof selectedRole.permissions === 'string'
				? JSON.parse(selectedRole.permissions || '{}')
				: selectedRole.permissions || {}
			setPermissions(rolePermissions)
		}
	}, [isEditMode, selectedRole, setValue])

	/**
	 * Handle form submission
	 */
	const onSubmit = async (data) => {
		try {
			if (isEditMode) {
				// Don't send slug when updating (it's immutable)
				const { slug, ...updateData } = data
				await updateRole(selectedRole.id, {
					...updateData,
					permissions: permissions,
				})
			} else {
				// Add company_id for new custom roles (hardcoded to 1 for now)
				// TODO: Get company_id from logged-in user's context
				const createData = {
					...data,
					company_id: 1,
					permissions: permissions,
				}
				await createRole(createData)
			}
			reset()
			setPermissions({})
			onClose()
		} catch (error) {
			// Error handled by store with toast
			console.error('Form submission error:', error)
		}
	}

	/**
	 * Generate slug from name
	 */
	const handleNameChange = (e) => {
		const name = e.target.value
		if (!isEditMode) {
			const slug = name
				.toLowerCase()
				.replace(/[^a-z0-9\s-]/g, '')
				.replace(/\s+/g, '-')
				.replace(/-+/g, '-')
			setValue('slug', slug)
		}
	}

	// Calculate permission count
	const permissionCount = Object.values(permissions).reduce(
		(sum, actions) => sum + (Array.isArray(actions) ? actions.length : 0),
		0
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			size="large"
			title={isEditMode ? 'Edit Role' : 'Create New Role'}
			closeOnBackdrop={false}
			closeOnEscape={false}
			footer={
				<>
					<Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
						<Button
							type="submit"
							variant="primary"
							loading={loading}
							onClick={handleSubmit(onSubmit)}
						>
							{isEditMode ? 'Update Role' : 'Create Role'}
						</Button>
					</div>
				</>
			}
		>
			<div className="role-form">
				{/* System Role Info */}
				{isSystemRole && isEditMode && (
					<Alert variant="info" className="role-form__warning">
						You are editing a system default role. Changes will apply to all companies using this role.
					</Alert>
				)}

				{/* Tabs */}
				<div className="role-form__tabs">
					<button
						type="button"
						className={`role-form__tab ${activeTab === 'details' ? 'role-form__tab--active' : ''}`}
						onClick={() => setActiveTab('details')}
					>
						Role Details
					</button>
					<button
						type="button"
						className={`role-form__tab ${activeTab === 'permissions' ? 'role-form__tab--active' : ''}`}
						onClick={() => setActiveTab('permissions')}
					>
						Permissions
						{permissionCount > 0 && (
							<span className="role-form__tab-badge">{permissionCount}</span>
						)}
					</button>
				</div>

				{/* Details Tab */}
				{activeTab === 'details' && (
					<form onSubmit={handleSubmit(onSubmit)} className="role-form__fields">
						{/* Role Name */}
						<Input
							label="Role Name"
							placeholder="e.g., Compliance Manager"
							{...register('name')}
							onChange={(e) => {
								register('name').onChange(e)
								handleNameChange(e)
							}}
							error={errors.name?.message}
							required
							disabled={loading}
						/>

						{/* Slug */}
						<Input
							label="Slug"
							placeholder="e.g., compliance-manager"
							{...register('slug')}
							error={errors.slug?.message}
							required
							disabled={isEditMode || loading}
							helperText={isEditMode ? 'Slug cannot be changed after creation' : 'Auto-generated from name'}
						/>

						{/* Description */}
						<Textarea
							label="Description"
							placeholder="Describe the role and its responsibilities..."
							rows={4}
							error={errors.description?.message}
							disabled={loading}
							{...register('description')}
						/>
					</form>
				)}

				{/* Permissions Tab */}
				{activeTab === 'permissions' && (
					<div className="role-form__permissions">
						<PermissionEditor
							value={permissions}
							onChange={setPermissions}
							disabled={loading}
							showSearch={true}
							expandedByDefault={false}
						/>
					</div>
				)}
			</div>
		</Modal>
	)
}

export default RoleForm
